import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BPMTable, DownloadDropdown, FilterBar, FullPageSpinner, Page } from "../Components";
import "./DataFreshness.scss";
import { useSetError } from "../redux/modals";
import { useSelector } from "react-redux";
import { currentCompanySelector } from "../redux/company";
import { awaitJSON, CrossChannelLambdaFetch } from "../utils/fetch-utils";
import { useStateFunction } from "../utils/hooks/useData";
import { exportToExcel } from "../utils/download-utils";

export const dataFreshnessDataHeaders = [
  {
    name: "company",
    label: "Company",
    flex: 1,
  },
  {
    name: "source",
    label: "Source",
    flex: 1,
  },
  {
    name: "account_id",
    label: "Account ID",
    flex: 1,
  },
  {
    name: "account_name",
    label: "Account Name",
    flex: 1,
  },
  {
    name: "table_name",
    label: "Table Name",
    flex: 1,
  },
  {
    name: "last_updated",
    label: "Last Updated",
    flex: 1,
  },
];

const DataFreshness: React.FC = () => {
  const company = useSelector(currentCompanySelector);
  const setError = useSetError();
  const [dataFreshnessRows, setDataFreshnessRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      try {
        const res = await CrossChannelLambdaFetch("/getDataFreshnessRun", {
          params: { company },
        });
        const json = await awaitJSON<any>(res);
        setDataFreshnessRows(json);
      } catch (error: any) {
        setError({
          message: `Failed to load data freshness: ${error.message}`,
          reportError: error,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetch();
  }, [company, setError]);

  const [filter, setFilter] = useStateFunction<(line: any) => boolean>(() => true);

  const filteredData = useMemo(() => {
    return dataFreshnessRows?.filter(filter) || [];
  }, [dataFreshnessRows, filter]);

  const excelDownloadModelOverview = useCallback(() => {
    exportToExcel(filteredData, "data_freshness");
  }, [filteredData]);

  return (
    <Page app2Redesign title="Data Freshness" pageType="Data Freshness">
      {isLoading ? (
        <FullPageSpinner />
      ) : dataFreshnessRows && dataFreshnessRows.length === 0 ? (
        <div className="dataFreshness">
          <div className="dataFreshnessContainer">
            <div className="dataFreshnessTableHeader">
              <div className="dataFreshnessTableHeaderTitle">No data available for {company}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="dataFreshness">
          <div className="dataFreshnessContainer">
            <div className="dataFreshnessTableHeader">
              <FilterBar
                hasAdvanced
                onFilter={setFilter}
                lines={dataFreshnessRows}
                options={dataFreshnessDataHeaders}
              />
              <DownloadDropdown
                size="sm"
                onClickOptions={[excelDownloadModelOverview]}
                menuOptions={["XLSX"]}
              />
            </div>
            <div className="dataFreshnessTableContainer">
              <div className="dataFreshnessTable">
                <BPMTable
                  data={filteredData}
                  headers={dataFreshnessDataHeaders}
                  filterBar={false}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
};

export default DataFreshness;
